import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { db } from "../firebase";
import { useAuth } from "../AuthContext";
import { useToast } from "../components/Toast";
import { useLoading } from "../components/LoadingBar";
import Navbar from "../components/Navbar";
import StatusBadge from "../components/StatusBadge";
import ChatBubble from "../components/ChatBubble";
import { SkeletonList } from "../components/Skeleton";
import { triggerConfetti } from "../components/Confetti";
import {
  SERVICES, US_STATES, sanitize, isValidZip, formatFullDate,
  copyToClipboard, MAX_ORDERS_PER_HOUR, ORDER_STATUSES,
  generateReceipt, getUPSTrackingUrl, playNotificationSound,
  saveDraft, loadDraft, clearDraft, estimateShipping,
} from "../config";
import {
  collection, addDoc, query, where, orderBy, onSnapshot, doc, updateDoc,
  getDoc, deleteDoc, Timestamp,
} from "firebase/firestore";

const emptyForm = {
  senderName: "", senderStreet: "", senderApt: "", senderCity: "", senderState: "NY", senderZip: "",
  recipientName: "", recipientStreet: "", recipientApt: "", recipientCity: "", recipientState: "NY", recipientZip: "",
  service: "ground", weight: "", length: "", width: "", height: "", notes: "", insurance: false, signature: false,
};

export default function Dashboard() {
  const { user } = useAuth();
  const toast = useToast();
  const { start: startLoading, stop: stopLoading } = useLoading();

  const [view, setView] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [addressBook, setAddressBook] = useState([]);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [newAddressName, setNewAddressName] = useState("");
  const [balance, setBalance] = useState(0);
  const [showZelleInfo, setShowZelleInfo] = useState(false);

  /* ─── Effects ─── */
  useEffect(() => {
    if (!user?.uid) return;
    const loadUserProfile = async () => {
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const d = userDoc.data();
          if (d.defaultAddress && d.defaultAddress.name) {
            setForm(prev => ({ ...prev,
              senderName: d.defaultAddress.name || "", senderStreet: d.defaultAddress.street || "",
              senderApt: d.defaultAddress.apt || "", senderCity: d.defaultAddress.city || "",
              senderState: d.defaultAddress.state || "NY", senderZip: d.defaultAddress.zip || "",
            }));
          }
        }
      } catch (e) { console.error("Error loading user profile:", e); }
    };
    loadUserProfile();
  }, [user?.uid]);

  // Listen for balance changes in real-time
  useEffect(() => {
    if (!user?.uid) return;
    const unsub = onSnapshot(doc(db, "users", user.uid), (snap) => {
      if (snap.exists()) setBalance(parseFloat(snap.data().balance) || 0);
    });
    return unsub;
  }, [user?.uid]);

  useEffect(() => { const draft = loadDraft(); if (draft) setForm(draft); }, []);

  useEffect(() => {
    if (view !== "new") return;
    const timer = setTimeout(() => saveDraft(form), 500);
    return () => clearTimeout(timer);
  }, [form, view]);

  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db, "orders"), where("userId", "==", user.uid), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, snap => {
      setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setOrdersLoading(false);
    }, err => { console.error("Error loading orders:", err); setOrdersLoading(false); toast.error("Failed to load orders"); });
    return () => unsub();
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db, "addresses"), where("userId", "==", user.uid));
    const unsub = onSnapshot(q, snap => setAddressBook(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => unsub();
  }, [user?.uid]);

  /* ─── Computed ─── */
  const filteredOrders = useMemo(() => {
    let f = orders;
    if (statusFilter !== "all") f = f.filter(o => o.status === statusFilter);
    if (searchQuery) { const q = searchQuery.toLowerCase(); f = f.filter(o => o.id.toLowerCase().includes(q) || o.recipientName?.toLowerCase().includes(q) || o.trackingNumber?.toLowerCase().includes(q)); }
    return f;
  }, [orders, statusFilter, searchQuery]);

  const counts = useMemo(() => {
    const c = { all: orders.length };
    Object.keys(ORDER_STATUSES).forEach(s => { c[s] = orders.filter(o => o.status === s).length; });
    c.totalSpent = orders.filter(o => o.status === "completed").reduce((s, o) => s + (parseFloat(o.quotedPrice) || o.price || 0), 0);
    return c;
  }, [orders]);

  /* ─── Handlers ─── */
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  }, []);

  const handleSaveAddress = async () => {
    if (!newAddressName.trim()) { toast.error("Please enter a name"); return; }
    if (!form.senderName || !form.senderStreet || !form.senderCity || !form.senderZip) { toast.error("Fill in all address fields"); return; }
    try {
      await addDoc(collection(db, "addresses"), { userId: user.uid, name: newAddressName, street: form.senderStreet, apt: form.senderApt, city: form.senderCity, state: form.senderState, zip: form.senderZip, createdAt: Timestamp.now() });
      toast.success("Address saved!"); setNewAddressName("");
    } catch (e) { console.error(e); toast.error("Failed to save address"); }
  };

  const loadAddressIntoForm = (a) => { setForm(p => ({ ...p, senderName: a.name, senderStreet: a.street, senderApt: a.apt, senderCity: a.city, senderState: a.state, senderZip: a.zip })); setShowAddressModal(false); };

  const handleDuplicateOrder = (o) => {
    setForm({ senderName: o.senderName, senderStreet: o.senderStreet, senderApt: o.senderApt, senderCity: o.senderCity, senderState: o.senderState, senderZip: o.senderZip, recipientName: o.recipientName, recipientStreet: o.recipientStreet, recipientApt: o.recipientApt, recipientCity: o.recipientCity, recipientState: o.recipientState, recipientZip: o.recipientZip, service: o.service, weight: o.weight, length: o.length, width: o.width, height: o.height, notes: o.notes, insurance: o.insurance || false, signature: o.signature || false });
    setView("new");
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    if (!form.senderName || !form.senderStreet || !form.senderCity || !form.senderZip || !form.recipientName || !form.recipientStreet || !form.recipientCity || !form.recipientZip || !form.weight || !form.service) { toast.error("Please fill in all required fields"); return; }
    if (!isValidZip(form.senderZip) || !isValidZip(form.recipientZip)) { toast.error("Invalid ZIP code"); return; }
    const oneHourAgo = new Date(Date.now() - 3600000);
    if (orders.filter(o => (o.createdAt.toDate ? o.createdAt.toDate() : o.createdAt) > oneHourAgo).length >= MAX_ORDERS_PER_HOUR) { toast.error(`Max ${MAX_ORDERS_PER_HOUR} orders/hour`); return; }
    startLoading();
    try {
      await addDoc(collection(db, "orders"), {
        senderName: sanitize(form.senderName), senderStreet: sanitize(form.senderStreet), senderApt: sanitize(form.senderApt || ""), senderCity: sanitize(form.senderCity), senderState: form.senderState, senderZip: form.senderZip,
        recipientName: sanitize(form.recipientName), recipientStreet: sanitize(form.recipientStreet), recipientApt: sanitize(form.recipientApt || ""), recipientCity: sanitize(form.recipientCity), recipientState: form.recipientState, recipientZip: form.recipientZip,
        service: form.service, weight: form.weight, length: form.length || "", width: form.width || "", height: form.height || "",
        notes: sanitize(form.notes || ""), insurance: form.insurance || false, signature: form.signature || false,
        userId: user.uid, userName: user.displayName || "", userEmail: user.email || "",
        orderId: "SF-" + Date.now().toString(36).toUpperCase(), status: "pending",
        finalCost: 0, createdAt: Timestamp.now(),
        quotedAt: null, confirmedAt: null, completedAt: null, voidedAt: null, cancelledAt: null, trackingNumber: null, voidedTrackingNumber: null,
      });
      toast.success("Order created!"); clearDraft(); setForm(emptyForm); setView("orders");
    } catch (e) { console.error(e); toast.error("Failed to create order"); }
    finally { stopLoading(); }
  };

  const handleUpdateStatus = async (id, s) => {
    try {
      // Check balance before confirming (read-only check — deduction happens admin-side)
      if (s === "confirmed") {
        const order = orders.find(o => o.id === id);
        const price = parseFloat(order?.quotedPrice || order?.price || 0);
        if (price > 0 && balance < price) {
          toast.error(`Insufficient balance ($${balance.toFixed(2)}). Add funds via Zelle to confirm this order ($${price.toFixed(2)} needed).`);
          setShowZelleInfo(true);
          return;
        }
      }
      const updates = { status: s };
      const ts = Timestamp.now();
      if (s === "quoted") updates.quotedAt = ts;
      else if (s === "confirmed") updates.confirmedAt = ts;
      else if (s === "completed") { updates.completedAt = ts; playNotificationSound("success"); triggerConfetti(); }
      else if (s === "void_requested") updates.voidedAt = ts;
      else if (s === "cancelled") updates.cancelledAt = ts;
      await updateDoc(doc(db, "orders", id), updates);
      toast.success(s === "confirmed" ? "Order confirmed! Balance will be deducted." : "Status updated");
    } catch (e) { console.error(e); toast.error("Failed to update"); }
  };

  const fmtTime = (t) => { if (!t) return null; return formatFullDate(t.toDate ? t.toDate() : t); };

  /* ─── Address Autocomplete (Geoapify — fast, accurate, 3000 req/day free) ─── */
  const GEOAPIFY_KEY = "07aa21b512954677840abfad9984885d";
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  const [addrLoading, setAddrLoading] = useState(false);
  const [activeAddrField, setActiveAddrField] = useState(null);
  const addrTimeoutRef = useRef(null);
  const addrAbortRef = useRef(null);

  const fetchAddressSuggestions = useCallback((inputValue) => {
    if (addrTimeoutRef.current) clearTimeout(addrTimeoutRef.current);
    if (addrAbortRef.current) addrAbortRef.current.abort();
    if (!inputValue || inputValue.length < 3) { setAddrSuggestions([]); setAddrLoading(false); return; }
    setAddrLoading(true);
    addrTimeoutRef.current = setTimeout(async () => {
      const controller = new AbortController();
      addrAbortRef.current = controller;
      try {
        const res = await fetch(
          `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(inputValue)}&lang=en&limit=5&filter=countrycode:us&format=json&apiKey=${GEOAPIFY_KEY}`,
          { signal: controller.signal }
        );
        if (!res.ok) { setAddrSuggestions([]); setAddrLoading(false); return; }
        const data = await res.json();
        const seen = new Set();
        const results = (data.results || [])
          .filter(r => r.address_line1 || r.street)
          .map(r => {
            const street = r.address_line1 || [r.housenumber, r.street].filter(Boolean).join(" ");
            const city = r.city || r.town || r.county || "";
            const stateAbbr = r.state_code ? r.state_code.replace("US-", "") : (US_STATES.find(s => s.name.toLowerCase() === (r.state || "").toLowerCase())?.code || "");
            const zip = r.postcode || "";
            return { street, city, state: stateAbbr, zip };
          })
          .filter(r => {
            if (!r.street || !r.city) return false;
            const key = `${r.street}|${r.city}|${r.state}`.toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .map(r => ({ ...r, display: [r.street, r.city, r.state, r.zip].filter(Boolean).join(", ") }));
        setAddrSuggestions(results);
      } catch (err) {
        if (err.name !== "AbortError") { console.error("Address autocomplete error:", err); setAddrSuggestions([]); }
      }
      setAddrLoading(false);
    }, 150);
  }, []);

  const selectAddressSuggestion = (suggestion, prefix) => {
    setForm(prev => ({
      ...prev,
      [prefix + "Street"]: suggestion.street,
      [prefix + "City"]: suggestion.city,
      [prefix + "State"]: suggestion.state || prev[prefix + "State"],
      [prefix + "Zip"]: suggestion.zip || prev[prefix + "Zip"],
    }));
    setAddrSuggestions([]);
    setActiveAddrField(null);
  };

  /* ─── Render helpers ─── */
  const renderAddressFields = (prefix, title) => (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#d4a053", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 14 }}>{title}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ gridColumn: "1 / -1" }}>
          <input className="sf-input" name={prefix+"Name"} placeholder="Full Name" value={form[prefix+"Name"]} onChange={handleChange} />
        </div>
        <div style={{ gridColumn: "1 / -1", position: "relative" }}>
          <input className="sf-input" name={prefix+"Street"} placeholder="Street Address — start typing for suggestions"
            value={form[prefix+"Street"]}
            onChange={(e) => { handleChange(e); setActiveAddrField(prefix+"Street"); fetchAddressSuggestions(e.target.value); }}
            onFocus={() => setActiveAddrField(prefix+"Street")}
            onBlur={() => setTimeout(() => { setActiveAddrField(null); setAddrSuggestions([]); }, 200)}
            autoComplete="off" />
          {activeAddrField === prefix+"Street" && (addrSuggestions.length > 0 || addrLoading) && (
            <div style={{
              position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50,
              background: "rgba(15,23,42,0.98)", border: "1px solid rgba(212,160,83,0.3)",
              borderRadius: 10, marginTop: 4, overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
              backdropFilter: "blur(12px)",
            }}>
              {addrLoading && addrSuggestions.length === 0 && (
                <div style={{ padding: "12px 14px", textAlign: "center", color: "#64748b", fontSize: 12 }}>
                  <span style={{ display: "inline-block", animation: "spin 1s linear infinite", marginRight: 8 }}>⟳</span>Searching addresses...
                </div>
              )}
              {addrSuggestions.map((s, i) => (
                <button key={i} type="button"
                  onMouseDown={(e) => { e.preventDefault(); selectAddressSuggestion(s, prefix); }}
                  style={{
                    display: "block", width: "100%", textAlign: "left", padding: "10px 14px",
                    border: "none", borderBottom: i < addrSuggestions.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none",
                    background: "transparent", color: "#e2e8f0", fontSize: 13, cursor: "pointer",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => e.target.style.background = "rgba(212,160,83,0.1)"}
                  onMouseLeave={e => e.target.style.background = "transparent"}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <span style={{ color: "#d4a053", fontSize: 14, flexShrink: 0, marginTop: 1 }}>📍</span>
                    <div>
                      <div style={{ fontWeight: 600, color: "#f1f5f9" }}>{s.street}</div>
                      <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{[s.city, s.state, s.zip].filter(Boolean).join(", ")}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <input className="sf-input" name={prefix+"Apt"} placeholder="Apt / Suite (optional)" value={form[prefix+"Apt"]} onChange={handleChange} />
        </div>
        <input className="sf-input" name={prefix+"City"} placeholder="City" value={form[prefix+"City"]} onChange={handleChange} />
        <select className="sf-select" name={prefix+"State"} value={form[prefix+"State"]} onChange={handleChange}>
          {US_STATES.map(s => <option key={s.code} value={s.code}>{s.code} — {s.name}</option>)}
        </select>
        <div style={{ gridColumn: "1 / -1" }}>
          <input className="sf-input" name={prefix+"Zip"} placeholder="ZIP Code" value={form[prefix+"Zip"]} onChange={handleChange} />
        </div>
      </div>
      {prefix === "sender" && (
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowAddressModal(true)} style={{ flex: 1 }}>Address Book</button>
        </div>
      )}
    </div>
  );

  /* ─── RENDER ─── */
  if (ordersLoading && view === "orders") {
    return (
      <div className="page-bg"><Navbar /><div className="container"><SkeletonList count={5} /></div></div>
    );
  }

  return (
    <div className="page-bg">
      <Navbar />
      <div className="container">
        <div className="animate-in">

          {/* HEADER */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div>
              <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 4 }}>
                {view === "orders" ? "Dashboard" : "New Shipment"}
              </h1>
              <p style={{ color: "#64748b", fontSize: 14, marginBottom: 0 }}>
                {view === "orders" ? "Track your orders and manage shipments." : "Fill in the details below to create a new order."}
              </p>
            </div>
            {view === "orders" ? (
              <button className="btn btn-primary" onClick={() => setView("new")}>
                + Create Order
              </button>
            ) : (
              <button className="btn btn-ghost" onClick={() => { setView("orders"); setForm(emptyForm); }}>
                Cancel
              </button>
            )}
          </div>

          {view === "orders" ? (
            <>
              {/* STATS ROW — matching admin style */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 12, marginBottom: 28, marginTop: 28 }}>
                {[
                  { key: "all", label: "Total", color: "#d4a053" },
                  { key: "pending", label: "Pending", color: "#f59e0b" },
                  { key: "quoted", label: "Quoted", color: "#3b82f6" },
                  { key: "confirmed", label: "Confirmed", color: "#8b5cf6" },
                  { key: "completed", label: "Completed", color: "#10b981" },
                ].map((s) => (
                  <div
                    key={s.key}
                    className={`card ${statusFilter === s.key ? "stat-card-selected" : ""}`}
                    style={{ padding: 16, textAlign: "center", cursor: "pointer" }}
                    onClick={() => setStatusFilter(s.key)}
                  >
                    <p style={{ fontSize: 28, fontWeight: 800, color: s.color, lineHeight: 1 }}>
                      {s.key === "all" ? counts.all : (counts[s.key] || 0)}
                    </p>
                    <p style={{ fontSize: 10, color: "#64748b", marginTop: 4, letterSpacing: 0.5, textTransform: "uppercase" }}>
                      {s.label}
                    </p>
                  </div>
                ))}
                <div className="card" style={{ padding: 16, textAlign: "center", cursor: "pointer", border: balance > 0 ? "1px solid rgba(16,185,129,0.2)" : "1px solid rgba(239,68,68,0.2)" }}
                  onClick={() => setShowZelleInfo(!showZelleInfo)}>
                  <p style={{ fontSize: 28, fontWeight: 800, color: balance > 0 ? "#10b981" : "#ef4444", lineHeight: 1 }}>
                    ${balance.toFixed(2)}
                  </p>
                  <p style={{ fontSize: 10, color: "#64748b", marginTop: 4, letterSpacing: 0.5, textTransform: "uppercase" }}>
                    Balance
                  </p>
                </div>
                <div className="card" style={{ padding: 16, textAlign: "center" }}>
                  <p style={{ fontSize: 28, fontWeight: 800, color: "#10b981", lineHeight: 1 }}>
                    ${counts.totalSpent?.toFixed(2) || "0.00"}
                  </p>
                  <p style={{ fontSize: 10, color: "#64748b", marginTop: 4, letterSpacing: 0.5, textTransform: "uppercase" }}>
                    Total Spent
                  </p>
                </div>
              </div>

              {/* ZELLE / ADD FUNDS INFO */}
              {showZelleInfo && (
                <div className="card animate-slide-up" style={{ padding: 20, marginBottom: 20,
                  border: "1px solid rgba(212,160,83,0.2)", background: "rgba(212,160,83,0.03)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <h3 style={{ fontSize: 16, fontWeight: 800, color: "#d4a053", marginBottom: 8 }}>
                        💳 Add Funds via Zelle
                      </h3>
                      <p style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.6, marginBottom: 12 }}>
                        Send your payment via Zelle to the address below. Once received, your balance will be credited and you can confirm orders.
                      </p>
                      <div style={{ padding: 14, borderRadius: 10, background: "rgba(15,23,42,0.5)",
                        border: "1px solid rgba(255,255,255,0.06)", marginBottom: 12 }}>
                        <p style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Send Zelle To</p>
                        <p style={{ fontSize: 18, fontWeight: 800, color: "#e2e8f0", fontFamily: "monospace" }}>
                          salesmanager@photonsells.com
                        </p>
                      </div>
                      <p style={{ fontSize: 11, color: "#64748b", fontStyle: "italic" }}>
                        Include your account email in the Zelle memo. Balance usually updates within a few minutes after payment is confirmed.
                      </p>
                    </div>
                    <button onClick={() => setShowZelleInfo(false)} style={{ background: "none", border: "none",
                      color: "#64748b", cursor: "pointer", fontSize: 18, padding: 4 }}>✕</button>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                    <div style={{ flex: 1, padding: 12, borderRadius: 8, background: "rgba(255,255,255,0.03)", textAlign: "center" }}>
                      <p style={{ fontSize: 22, fontWeight: 800, color: balance > 0 ? "#10b981" : "#ef4444" }}>${balance.toFixed(2)}</p>
                      <p style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase" }}>Current Balance</p>
                    </div>
                  </div>
                </div>
              )}

              {/* SEARCH */}
              <div style={{ marginBottom: 20 }}>
                <input
                  className="sf-input"
                  type="text"
                  placeholder="Search orders, customers, tracking..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ marginBottom: 12, maxWidth: 400 }}
                />
                {/* FILTER PILLS */}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  {["all", "pending", "quoted", "confirmed", "completed", "void_requested", "voided", "cancelled"].map((f) => (
                    <button
                      key={f}
                      onClick={() => setStatusFilter(f)}
                      style={{
                        padding: "7px 18px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer",
                        border: "1px solid",
                        borderColor: statusFilter === f ? "rgba(212,160,83,0.4)" : "rgba(255,255,255,0.06)",
                        background: statusFilter === f ? "rgba(212,160,83,0.1)" : "transparent",
                        color: statusFilter === f ? "#d4a053" : "#64748b",
                        transition: "all 0.2s", textTransform: "capitalize", fontFamily: "'DM Sans', sans-serif",
                      }}
                    >
                      {f === "void_requested" ? "Void Req" : (ORDER_STATUSES[f]?.label || f)} ({f === "all" ? counts.all : (counts[f] || 0)})
                    </button>
                  ))}
                </div>
              </div>

              {/* ORDERS LIST — admin-style flat cards */}
              {filteredOrders.length === 0 ? (
                <div className="card" style={{ padding: 60, textAlign: "center" }}>
                  <p style={{ fontSize: 40, marginBottom: 12, opacity: 0.5 }}>&#128230;</p>
                  <p style={{ color: "#94a3b8", fontSize: 16, fontWeight: 500 }}>No shipments found</p>
                  <p style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}>Create your first order to get started</p>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {filteredOrders.map((order) => {
                    const svc = SERVICES.find((s) => s.id === order.service) || { name: order.service };
                    return (
                      <div key={order.id} className="card order-card" style={{ padding: 20 }}>
                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>

                          {/* Left: order info */}
                          <div style={{ flex: 1, minWidth: 280 }}>
                            {/* Header row */}
                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                              <span style={{ color: "#64748b", fontSize: 12, fontFamily: "monospace" }}>
                                #{order.orderId || order.id.slice(0, 8)}
                              </span>
                              <StatusBadge status={order.status} />
                            </div>

                            {/* From → To */}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 16, alignItems: "start" }} className="grid-2">
                              <div>
                                <p style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>From</p>
                                <p style={{ color: "#e2e8f0", fontSize: 14, fontWeight: 600 }}>{order.senderName}</p>
                                <p style={{ color: "#94a3b8", fontSize: 13 }}>{order.senderStreet}</p>
                                <p style={{ color: "#94a3b8", fontSize: 13 }}>
                                  {order.senderCity}, {order.senderState} {order.senderZip}
                                </p>
                              </div>
                              <div className="hide-mobile" style={{ color: "#d4a053", fontSize: 22, alignSelf: "center" }}>→</div>
                              <div>
                                <p style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>To</p>
                                <p style={{ color: "#e2e8f0", fontSize: 14, fontWeight: 600 }}>{order.recipientName}</p>
                                <p style={{ color: "#94a3b8", fontSize: 13 }}>{order.recipientStreet}</p>
                                <p style={{ color: "#94a3b8", fontSize: 13 }}>
                                  {order.recipientCity}, {order.recipientState} {order.recipientZip}
                                </p>
                              </div>
                            </div>

                            {/* Details row */}
                            <div style={{ display: "flex", gap: 16, marginTop: 12, flexWrap: "wrap" }}>
                              <span style={{ fontSize: 12, color: "#64748b" }}>
                                Service: <strong style={{ color: "#94a3b8" }}>{svc.name}</strong>
                              </span>
                              {order.weight && (
                                <span style={{ fontSize: 12, color: "#64748b" }}>
                                  Weight: <strong style={{ color: "#94a3b8" }}>{order.weight} lbs</strong>
                                </span>
                              )}
                              {order.length && (
                                <span style={{ fontSize: 12, color: "#64748b" }}>
                                  Dims: <strong style={{ color: "#94a3b8" }}>{order.length}x{order.width}x{order.height} in</strong>
                                </span>
                              )}
                              {(order.quotedPrice || order.price) && (
                                <span style={{ fontSize: 12, color: "#64748b" }}>
                                  Quoted: <strong style={{ color: "#d4a053" }}>${order.quotedPrice || order.price?.toFixed(2)}</strong>
                                </span>
                              )}
                              {order.trackingNumber && (
                                <span style={{ fontSize: 12, color: "#64748b" }}>
                                  Tracking:{" "}
                                  <a
                                    href={getUPSTrackingUrl(order.trackingNumber)}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ color: "#10b981", fontWeight: 600, textDecoration: "none" }}
                                  >
                                    {order.trackingNumber}
                                  </a>
                                  <button
                                    onClick={() => { copyToClipboard(order.trackingNumber); toast.success("Copied!"); }}
                                    style={{ marginLeft: 6, background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: 11 }}
                                  >
                                    Copy
                                  </button>
                                </span>
                              )}
                              {order.insurance && (
                                <span style={{ fontSize: 12, color: "#f59e0b" }}>Insurance</span>
                              )}
                              {order.signature && (
                                <span style={{ fontSize: 12, color: "#8b5cf6" }}>Signature Req.</span>
                              )}
                            </div>
                          </div>

                          {/* Right: customer actions */}
                          <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end", minWidth: 180 }}>

                            {/* PENDING — waiting */}
                            {order.status === "pending" && (
                              <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                                <p style={{ fontSize: 13, color: "#f59e0b", fontStyle: "italic" }}>
                                  Awaiting quote from admin
                                </p>
                                <button
                                  className="btn btn-sm"
                                  style={{ background: "rgba(239,68,68,0.12)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.25)" }}
                                  onClick={() => handleUpdateStatus(order.id, "cancelled")}
                                >
                                  Cancel Order
                                </button>
                              </div>
                            )}

                            {/* QUOTED — Confirm or Cancel */}
                            {order.status === "quoted" && (
                              <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
                                <p style={{ fontSize: 13, color: "#3b82f6", fontWeight: 600 }}>
                                  Quote: ${order.quotedPrice || order.price?.toFixed(2)}
                                </p>
                                {(() => {
                                  const price = parseFloat(order.quotedPrice || order.price || 0);
                                  const canAfford = balance >= price;
                                  return !canAfford ? (
                                    <p style={{ fontSize: 11, color: "#ef4444", margin: "2px 0" }}>
                                      Insufficient balance (${balance.toFixed(2)}).{" "}
                                      <span style={{ color: "#d4a053", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => setShowZelleInfo(true)}>Add funds</span>
                                    </p>
                                  ) : (
                                    <p style={{ fontSize: 11, color: "#10b981", margin: "2px 0" }}>
                                      Balance after: ${(balance - price).toFixed(2)}
                                    </p>
                                  );
                                })()}
                                <div style={{ display: "flex", gap: 8 }}>
                                  <button
                                    className="btn btn-sm"
                                    style={{ flex: 1, background: "rgba(16,185,129,0.12)", color: "#10b981", border: "1px solid rgba(16,185,129,0.25)" }}
                                    onClick={() => handleUpdateStatus(order.id, "confirmed")}
                                  >
                                    Confirm & Pay
                                  </button>
                                  <button
                                    className="btn btn-sm"
                                    style={{ flex: 1, background: "rgba(239,68,68,0.12)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.25)" }}
                                    onClick={() => handleUpdateStatus(order.id, "cancelled")}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* CONFIRMED — Waiting for label */}
                            {order.status === "confirmed" && (
                              <p style={{ fontSize: 13, color: "#8b5cf6", fontStyle: "italic" }}>
                                Label being prepared...
                              </p>
                            )}

                            {/* COMPLETED — View Label + Actions */}
                            {order.status === "completed" && (
                              <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
                                {(order.labelData || order.labelUrl) && (
                                  <button
                                    onClick={() => {
                                      const data = order.labelData || order.labelUrl;
                                      const link = document.createElement("a");
                                      link.href = data;
                                      link.download = order.labelFileName || "label.pdf";
                                      link.click();
                                    }}
                                    style={{
                                      display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 18px",
                                      borderRadius: 10, background: "rgba(16,185,129,0.1)", color: "#10b981",
                                      fontSize: 13, fontWeight: 600, cursor: "pointer",
                                      border: "1px solid rgba(16,185,129,0.2)", fontFamily: "'DM Sans', sans-serif",
                                    }}
                                  >
                                    Download Label
                                  </button>
                                )}
                                <div style={{ display: "flex", gap: 8 }}>
                                  <button
                                    className="btn btn-sm"
                                    style={{ flex: 1, background: "rgba(139,92,246,0.12)", color: "#8b5cf6", border: "1px solid rgba(139,92,246,0.25)" }}
                                    onClick={() => generateReceipt(order)}
                                  >
                                    Receipt
                                  </button>
                                  <button
                                    className="btn btn-sm"
                                    style={{ flex: 1, background: "rgba(249,115,22,0.12)", color: "#f97316", border: "1px solid rgba(249,115,22,0.25)" }}
                                    onClick={() => handleUpdateStatus(order.id, "void_requested")}
                                  >
                                    Void
                                  </button>
                                </div>
                                <button
                                  className="btn btn-sm"
                                  style={{ background: "rgba(59,130,246,0.12)", color: "#3b82f6", border: "1px solid rgba(59,130,246,0.25)", width: "100%" }}
                                  onClick={() => handleDuplicateOrder(order)}
                                >
                                  Reorder
                                </button>
                              </div>
                            )}

                            {/* VOID_REQUESTED */}
                            {order.status === "void_requested" && (
                              <p style={{ fontSize: 13, color: "#f97316", fontStyle: "italic" }}>Void request pending...</p>
                            )}

                            {/* VOIDED */}
                            {order.status === "voided" && (
                              <p style={{ fontSize: 13, color: "#6b7280", fontStyle: "italic" }}>Label voided</p>
                            )}

                            {/* CANCELLED */}
                            {order.status === "cancelled" && (
                              <p style={{ fontSize: 13, color: "#ef4444", fontStyle: "italic" }}>Order cancelled</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            /* ─── NEW ORDER FORM ─── */
            <form onSubmit={handleSubmitOrder} style={{ marginTop: 28 }}>
              <div className="card" style={{ padding: 28 }}>
                {renderAddressFields("sender", "From")}
                {renderAddressFields("recipient", "To")}

                {/* Package Details */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#d4a053", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 14 }}>Package Details</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <label className="sf-label">Service</label>
                      <select className="sf-select" name="service" value={form.service} onChange={handleChange}>
                        {SERVICES.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="sf-label">Weight (lbs)</label>
                      <input type="number" className="sf-input" name="weight" placeholder="0" value={form.weight} onChange={handleChange} />
                    </div>
                    <div>
                      <label className="sf-label">Length</label>
                      <input type="number" className="sf-input" name="length" placeholder="0" value={form.length} onChange={handleChange} />
                    </div>
                    <div>
                      <label className="sf-label">Width</label>
                      <input type="number" className="sf-input" name="width" placeholder="0" value={form.width} onChange={handleChange} />
                    </div>
                    <div>
                      <label className="sf-label">Height</label>
                      <input type="number" className="sf-input" name="height" placeholder="0" value={form.height} onChange={handleChange} />
                    </div>
                  </div>
                  {parseFloat(form.weight) > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <p style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
                        Rate Comparison (Estimated)
                      </p>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
                        {SERVICES.map((svc) => {
                          const est = estimateShipping(svc.id, parseFloat(form.weight), {
                            length: parseFloat(form.length) || 0,
                            width: parseFloat(form.width) || 0,
                            height: parseFloat(form.height) || 0,
                          });
                          const isSelected = form.service === svc.id;
                          return (
                            <div
                              key={svc.id}
                              onClick={() => setForm((prev) => ({ ...prev, service: svc.id }))}
                              style={{
                                padding: "12px 14px",
                                borderRadius: 10,
                                cursor: "pointer",
                                border: isSelected ? "2px solid #d4a053" : "1px solid rgba(255,255,255,0.08)",
                                background: isSelected ? "rgba(212,160,83,0.08)" : "rgba(255,255,255,0.03)",
                                transition: "all 0.2s",
                                textAlign: "center",
                              }}
                            >
                              <div style={{ fontSize: 20, marginBottom: 4 }}>{svc.icon}</div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: isSelected ? "#d4a053" : "#e2e8f0", marginBottom: 2 }}>
                                {svc.name.replace("UPS ", "")}
                              </div>
                              <div style={{ fontSize: 10, color: "#64748b", marginBottom: 6 }}>{svc.eta}</div>
                              <div style={{ fontSize: 18, fontWeight: 800, color: isSelected ? "#d4a053" : "#10b981" }}>
                                ${est || "—"}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <p style={{ fontSize: 10, color: "#475569", marginTop: 8, fontStyle: "italic" }}>
                        Final price may vary. Click a service to select it.
                      </p>
                    </div>
                  )}
                </div>

                {/* Options */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#d4a053", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 14 }}>Options</div>
                  <div style={{ display: "flex", gap: 24 }}>
                    <label className="checkbox-label">
                      <input type="checkbox" name="insurance" checked={form.insurance} onChange={handleChange} />
                      Insurance
                    </label>
                    <label className="checkbox-label">
                      <input type="checkbox" name="signature" checked={form.signature} onChange={handleChange} />
                      Signature Required
                    </label>
                  </div>
                </div>

                {/* Notes */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#d4a053", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 14 }}>Notes</div>
                  <textarea
                    name="notes" placeholder="Special instructions..." value={form.notes} onChange={handleChange}
                    className="sf-input"
                    style={{ height: 80, resize: "vertical" }}
                  />
                </div>

                {/* Submit */}
                <div style={{ display: "flex", gap: 12 }}>
                  <button type="submit" className="btn btn-primary" style={{ flex: 1, fontSize: 15, padding: "14px 28px", justifyContent: "center" }}>
                    Submit Order
                  </button>
                  <button type="button" className="btn btn-ghost" onClick={() => { setView("orders"); setForm(emptyForm); }}>
                    Cancel
                  </button>
                </div>
              </div>

              {/* Save address section */}
              <div className="card" style={{ marginTop: 12, padding: 20, display: "flex", gap: 10, alignItems: "center" }}>
                <span style={{ fontSize: 13, color: "#86868b", whiteSpace: "nowrap" }}>Save sender address:</span>
                <input className="sf-input" style={{ flex: 1 }} placeholder="Label (e.g. Home, Office)" value={newAddressName} onChange={e => setNewAddressName(e.target.value)} />
                <button type="button" className="btn btn-success btn-sm" onClick={handleSaveAddress}>Save</button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* ADDRESS BOOK MODAL */}
      {showAddressModal && (
        <div className="modal-overlay" onClick={() => setShowAddressModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Address Book</h3>
            {addressBook.length === 0 ? (
              <div style={{ textAlign: "center", padding: "30px 0", color: "#64748b" }}>
                No saved addresses
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {addressBook.map(a => (
                  <button key={a.id} onClick={() => loadAddressIntoForm(a)}
                    className="address-book-item"
                    style={{ cursor: "pointer", textAlign: "left", background: "rgba(255,255,255,0.04)", fontFamily: "'DM Sans', sans-serif", color: "#e2e8f0" }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2 }}>{a.name}</div>
                      <div style={{ fontSize: 13, color: "#86868b" }}>{a.street} {a.apt}</div>
                      <div style={{ fontSize: 13, color: "#86868b" }}>{a.city}, {a.state} {a.zip}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            <button onClick={() => setShowAddressModal(false)} className="btn btn-ghost" style={{ width: "100%", marginTop: 16, justifyContent: "center" }}>Close</button>
          </div>
        </div>
      )}

      <ChatBubble orders={orders} />
    </div>
  );
}
